import { APP_INITIALIZER, ErrorHandler, Provider, enableProdMode, importProvidersFrom } from '@angular/core';
import { bootstrapApplication } from '@angular/platform-browser';
import { provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { RouteReuseStrategy, provideRouter } from '@angular/router';
import { IonicModule, IonicRouteStrategy } from '@ionic/angular';
import { IonicStorageModule } from '@ionic/storage-angular';
import { provideServiceWorker } from '@angular/service-worker';

import { AppComponent } from './app/app.component';
import { routes } from './app/app.routes';
import { environment } from './environments/environment';
import { LazySentryErrorHandler, SentryService } from './app/core/services/internal/sentry.service';
import { AnalyticsService } from './app/core/services/internal/analytics.service';
import { trustedScriptUrl } from './app/core/security/trusted-types';

if (environment.WORKSPACE === 'prod') {
  enableProdMode();
}

function initSentry(sentry: SentryService) {
  return () => sentry.initialize();
}

function initAnalytics(analytics: AnalyticsService) {
  return () => analytics.initialize();
}

function isFlagEnabled(value: unknown): boolean {
  return !!value && value !== 'OFF';
}

const sentryEnabled = isFlagEnabled((environment as any).SENTRY_DSN);
const analyticsEnabled =
  isFlagEnabled((environment as any).POSTHOG_API_KEY) ||
  isFlagEnabled((environment as any).GOOGLE_ANALYTICS_ID);

function resolveIonicMode(): 'ios' | 'md' | undefined {
  const raw = String((environment as any).APP_MODE ?? '')
    .replace(/['",]/g, '')
    .trim()
    .toLowerCase();
  if (raw === 'ios' || raw === 'md') return raw;
  return undefined;
}

const IONIC_MODE = resolveIonicMode();
const IONIC_CONFIG = IONIC_MODE ? { mode: IONIC_MODE } : {};

const sentryProviders: Provider[] = sentryEnabled
  ? [
      {
        provide: ErrorHandler,
        useClass: LazySentryErrorHandler,
      },
      {
        provide: APP_INITIALIZER,
        useFactory: initSentry,
        deps: [SentryService],
        multi: true,
      },
    ]
  : [];

const analyticsProviders: Provider[] = analyticsEnabled
  ? [
      {
        provide: APP_INITIALIZER,
        useFactory: initAnalytics,
        deps: [AnalyticsService],
        multi: true,
      },
    ]
  : [];

bootstrapApplication(AppComponent, {
  providers: [
    importProvidersFrom(
      IonicModule.forRoot(IONIC_CONFIG),
      IonicStorageModule.forRoot(),
    ),
    provideServiceWorker(trustedScriptUrl('/OneSignalSDKWorker.js') as string, {
      enabled: environment.production,
      scope: '/',
      registrationStrategy: 'registerWhenStable:30000',
    }),
    provideRouter(routes),
    provideHttpClient(withInterceptorsFromDi()),
    { provide: RouteReuseStrategy, useClass: IonicRouteStrategy },
    ...sentryProviders,
    ...analyticsProviders,
  ],
}).catch((err) => console.error(err));

if (typeof window !== 'undefined' && 'serviceWorker' in navigator) {
  window.addEventListener(
    'load',
    () => scheduleIdle(cleanupStaleServiceWorkers),
    { once: true },
  );
}

function scheduleIdle(task: () => void): void {
  const win = window as Window & {
    requestIdleCallback?: (
      callback: () => void,
      options?: { timeout: number },
    ) => number;
  };

  if (typeof win.requestIdleCallback === 'function') {
    win.requestIdleCallback(task, { timeout: 5000 });
    return;
  }

  window.setTimeout(task, 1500);
}

function cleanupStaleServiceWorkers(): void {
  try {
    navigator.serviceWorker.getRegistrations().then((registrations) => {
      registrations.forEach((registration) => {
        const workers = [
          registration.active,
          registration.installing,
          registration.waiting,
        ];
        const hasOneSignalWorker = workers.some((worker) =>
          worker?.scriptURL.includes('OneSignalSDKWorker'),
        );

        if (hasOneSignalWorker) return;

        registration.unregister().catch(() => undefined);
      });
    });
  } catch {}

  try {
    if (typeof caches === 'undefined' || !caches.keys) return;

    caches.keys().then((names) => {
      names.forEach((name) => {
        const isOldAngularCache =
          name.startsWith('ngsw:') || name.startsWith('ngsw-');
        const isOldWorkboxCache = name.startsWith('workbox-');
        const isOldAppShellCache = name.includes('app-shell');
        const isLegacyDbCache = name.includes(':db:');

        if (
          !isOldAngularCache &&
          !isOldWorkboxCache &&
          !isOldAppShellCache &&
          !isLegacyDbCache
        ) {
          return;
        }

        caches.delete(name).catch(() => undefined);
      });
    });
  } catch {}
}
